import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, unref as _unref, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport } from "vue";
const _hoisted_1 = { class: "ut-page" };
const _hoisted_2 = {
    key: 0,
    class: "ut-header"
};
const _hoisted_3 = {
    key: 1,
    class: "ut-header"
};
const _hoisted_4 = { class: "ut-count" };
const _hoisted_5 = {
    key: 3,
    class: "ut-search"
};
const _hoisted_6 = {
    key: 4,
    class: "ut-list"
};
const _hoisted_7 = ["onClick"];
const _hoisted_8 = ["src"];
const _hoisted_9 = { class: "ut-row-text" };
const _hoisted_10 = { class: "ut-row-name" };
const _hoisted_11 = { class: "ut-row-time" };
const _hoisted_12 = { class: "ut-row-right" };
const _hoisted_13 = { class: "ut-row-amount" };
const _hoisted_14 = ["src"];
const _hoisted_15 = {
    key: 5,
    class: "ut-empty"
};
const _hoisted_16 = {
    key: 6,
    class: "ut-empty"
};
const _hoisted_17 = {
    key: 7,
    class: "anchor-bottom ut-apply"
};
const _hoisted_18 = ["src"];
const _hoisted_19 = { class: "ut-toast-text" };
import { ref, computed, onMounted, useTemplateRef, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { useTransactions } from '@/composables/useTransactions';
import { usePolling } from '@/composables/usePolling';
import { getSpendingAnalysis, clearSpendingAnalysisCache, getMonthRangeForSelection } from '@/composables/useSpendingAnalysis';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import MGTextField from './elements/MGTextField.vue';
import ChangeCategoryDrawer from './elements/ChangeCategoryDrawer.vue';
import { selectedPeriod, selectedAccount, dateSelection, snapToSpendingCycle } from './elements/spendingAnalysisFilterState';
const PENDING_KEY = 'utPendingCategorisation';
const MAX_SELECTION = 10;
export default /*@__PURE__*/ _defineComponent({
    __name: 'UncategorisedTransactionsPage',
    setup(__props) {
        const state = useAppState();
        const router = useRouter();
        useAuth();
        const { getCustomer } = useCustomer();
        const { getBankingInfo } = useBanking();
        const transactionsApi = useTransactions();
        const { poll } = usePolling('uncategorisedBulkUpdate');
        const categoryDrawer = useTemplateRef('categoryDrawer');
        const cookies = getCurrentInstance()?.appContext.config.globalProperties.$cookies;
        const safeTop = computed(() => parseInt(cookies?.get('st') ?? '0'));
        const summary = ref(null);
        const loading = ref(true);
        const searchQuery = ref('');
        const selectMode = ref(false);
        const selectedIds = ref(new Set());
        const toastCount = ref(0);
        const limitToastVisible = ref(false);
        let toastTimer = null;
        let limitToastTimer = null;
        function isUncategorised(t) {
            return !t.subClassification || t.subClassification === 'Uncategorised' || t.subClassification === 'Uncategorized';
        }
        const uncategorisedTransactions = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            const flat = [];
            const seen = new Set();
            for (const p of parents) {
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    if (!isUncategorised(t))
                        continue;
                    const id = t.id ?? `${t.merchantName}-${t.timestamp}-${t.amount}`;
                    if (seen.has(id))
                        continue;
                    seen.add(id);
                    flat.push({
                        id,
                        merchantName: t.merchantName,
                        amount: t.amount,
                        timestamp: t.timestamp,
                    });
                }
            }
            flat.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            return flat;
        });
        const uncategorisedCount = computed(() => uncategorisedTransactions.value.length);
        const filteredTransactions = computed(() => {
            const q = searchQuery.value.trim().toLowerCase();
            if (!q)
                return uncategorisedTransactions.value;
            const friendlyAmount = q.includes('.') && q.endsWith('0') ? q.slice(0, -1) : q;
            return uncategorisedTransactions.value.filter(t => {
                if ((t.merchantName ?? '').toLowerCase().includes(q))
                    return true;
                if (t.amount?.toString().includes(friendlyAmount))
                    return true;
                return false;
            });
        });
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        function formatRowTimestamp(ts) {
            if (!ts)
                return '';
            const d = new Date(ts);
            if (Number.isNaN(d.getTime()))
                return '';
            const date = `${d.getDate()} ${monthNames[d.getMonth()]}, ${d.getFullYear()}`;
            if (d.getHours() === 0 && d.getMinutes() === 0)
                return date;
            const hh = String(d.getHours()).padStart(2, '0');
            const mm = String(d.getMinutes()).padStart(2, '0');
            return `${hh}:${mm}, ${date}`;
        }
        // Mirror SpendingAnalysisPage's analysisRange so this page reflects the same
        // period/date selection the user picked there.
        function selectedRange() {
            const monthRange = getMonthRangeForSelection(dateSelection.value, selectedPeriod.value);
            if (monthRange) {
                const [fy, fm] = monthRange.startMonth.split('-').map(Number);
                const [ty, tm] = monthRange.endMonth.split('-').map(Number);
                return {
                    fromDate: new Date(Date.UTC(fy, fm - 1, 1)),
                    toDate: new Date(Date.UTC(ty, tm, 1)),
                };
            }
            const now = new Date();
            let year = now.getUTCFullYear();
            let month = now.getUTCMonth();
            const cycleDay = state.bankInfo?.spendingCycle?.day;
            if (snapToSpendingCycle.value && cycleDay && now.getUTCDate() >= cycleDay) {
                month += 1;
                if (month > 11) {
                    month = 0;
                    year += 1;
                }
            }
            return {
                fromDate: new Date(Date.UTC(year, month, 1)),
                toDate: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59)),
            };
        }
        const selectedAccountIds = computed(() => {
            const key = selectedAccount.value;
            const accounts = (state.bankInfo?.accounts || []).filter((a) => a.source === 0);
            if (!accounts.length || key === 'all')
                return undefined;
            const typeMap = { current: 'Current', credit: 'Credit', savings: 'Savings' };
            if (typeMap[key]) {
                const ids = accounts.filter((a) => a.accountType?.prettyName === typeMap[key]).map((a) => a.id);
                return ids.length ? ids : undefined;
            }
            const match = accounts.find((a) => a.id === key);
            return match ? [match.id] : undefined;
        });
        function fetchSummary() {
            const { fromDate, toDate } = selectedRange();
            loading.value = true;
            getSpendingAnalysis(fromDate, toDate, {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            }, (result) => {
                if (result)
                    summary.value = result;
                loading.value = false;
                checkPendingCategorisation();
            }, true);
        }
        function checkPendingCategorisation(attempt = 0) {
            const raw = sessionStorage.getItem(PENDING_KEY);
            if (!raw)
                return;
            let pending = [];
            try {
                pending = JSON.parse(raw);
            }
            catch { /* ignore */ }
            if (!pending.length) {
                sessionStorage.removeItem(PENDING_KEY);
                return;
            }
            const stillUncategorised = new Set(uncategorisedTransactions.value.map(t => t.id));
            const categorised = pending.filter(id => !stillUncategorised.has(id));
            if (categorised.length === 0 && attempt < 4) {
                // Read-model lag — refetch and retry.
                const { fromDate, toDate } = selectedRange();
                getSpendingAnalysis(fromDate, toDate, {
                    accountIds: selectedAccountIds.value,
                    snapToSpendingCycle: snapToSpendingCycle.value,
                }, (result) => {
                    if (result)
                        summary.value = result;
                    window.setTimeout(() => checkPendingCategorisation(attempt + 1), 1000);
                }, true);
                return;
            }
            sessionStorage.removeItem(PENDING_KEY);
            showToast(categorised.length);
        }
        function toggleSelectMode() {
            selectMode.value = !selectMode.value;
            if (!selectMode.value)
                selectedIds.value = new Set();
        }
        function onRowClick(t) {
            if (!selectMode.value) {
                const existing = JSON.parse(sessionStorage.getItem(PENDING_KEY) || '[]');
                if (!existing.includes(t.id))
                    existing.push(t.id);
                sessionStorage.setItem(PENDING_KEY, JSON.stringify(existing));
                router.push('/TransactionDetail/' + t.id);
                return;
            }
            const next = new Set(selectedIds.value);
            if (next.has(t.id)) {
                next.delete(t.id);
            }
            else {
                if (next.size >= MAX_SELECTION) {
                    showLimitToast();
                    return;
                }
                next.add(t.id);
            }
            selectedIds.value = next;
        }
        function showLimitToast() {
            limitToastVisible.value = true;
            if (limitToastTimer)
                window.clearTimeout(limitToastTimer);
            limitToastTimer = window.setTimeout(() => { limitToastVisible.value = false; }, 3000);
        }
        function openMarkSelectedDrawer() {
            categoryDrawer.value?.openDrawer();
        }
        function pollUntilApplied(updatedIds) {
            let latest = null;
            const { fromDate, toDate } = selectedRange();
            const opts = {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            };
            poll({
                remaining: 5,
                interval: 1000,
                fetch: (cb) => {
                    getSpendingAnalysis(fromDate, toDate, opts, (result) => {
                        latest = result;
                        cb();
                    });
                },
                onPoll: () => { },
                shouldStop: () => {
                    if (!latest)
                        return false;
                    const parents = Array.isArray(latest.parentCategories) ? latest.parentCategories : [];
                    for (const p of parents) {
                        const txs = Array.isArray(p.transactions) ? p.transactions : [];
                        for (const t of txs) {
                            if (updatedIds.has(t.id) && isUncategorised(t))
                                return false;
                        }
                    }
                    return true;
                },
                onFinish: () => {
                    if (latest)
                        summary.value = latest;
                    categoryDrawer.value?.closeDrawer();
                    selectMode.value = false;
                    selectedIds.value = new Set();
                    showToast(updatedIds.size);
                },
            });
        }
        function showToast(count) {
            if (count <= 0)
                return;
            toastCount.value = count;
            if (toastTimer)
                window.clearTimeout(toastTimer);
            toastTimer = window.setTimeout(() => { toastCount.value = 0; }, 2000);
        }
        function onBulkCategoryApply(payload) {
            if (!payload.classification) {
                categoryDrawer.value?.closeDrawer();
                return;
            }
            const flatSelected = uncategorisedTransactions.value
                .filter((t) => selectedIds.value.has(t.id));
            if (!flatSelected.length) {
                categoryDrawer.value?.closeDrawer();
                return;
            }
            const updatedIds = new Set(flatSelected.map((t) => t.id));
            transactionsApi.applyCategoryToTransactions(flatSelected, payload, () => {
                clearSpendingAnalysisCache();
                pollUntilApplied(updatedIds);
            });
        }
        function onBack() {
            sessionStorage.removeItem(PENDING_KEY);
            router.back();
        }
        onMounted(() => {
            getCustomer(() => {
                getBankingInfo(() => {
                    fetchSummary();
                });
            });
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: "Uncategorised",
                    logicalBack: true,
                    onLogicalBack: onBack
                }),
                _createElementVNode("div", _hoisted_1, [
                    (loading.value && !summary.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createVNode(_component_v_skeleton_loader, {
                                type: "text",
                                width: "120",
                                class: "ut-skel-count"
                            }),
                            _createVNode(_component_v_skeleton_loader, {
                                type: "button",
                                class: "ut-skel-button"
                            })
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createElementVNode("span", _hoisted_4, _toDisplayString(uncategorisedCount.value) + " transaction" + _toDisplayString(uncategorisedCount.value === 1 ? '' : 's'), 1),
                            (uncategorisedCount.value > 0)
                                ? (_openBlock(), _createBlock(MGButton, {
                                    key: 0,
                                    text: selectMode.value ? (selectedIds.value.size > 0 ? `Deselect ${selectedIds.value.size}` : 'Cancel') : 'Select multiple',
                                    outline: "",
                                    small: "",
                                    noCaps: "",
                                    onClick: toggleSelectMode
                                }, null, 8, ["text"]))
                                : _createCommentVNode("", true)
                        ])),
                    (loading.value && !summary.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 2,
                            type: "list-item-two-line, list-item-two-line, list-item-two-line, list-item-two-line, list-item-two-line, list-item-two-line",
                            class: "ut-skel-list"
                        }))
                        : (uncategorisedCount.value > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                _createVNode(MGTextField, {
                                    modelValue: searchQuery.value,
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((searchQuery).value = $event)),
                                    "prepend-inner-icon": "mdi-magnify",
                                    density: "compact",
                                    label: "Search transactions",
                                    variant: "solo",
                                    flat: true,
                                    "hide-details": "",
                                    clearable: "",
                                    "single-line": "",
                                    "bg-color": "#eeeeee",
                                    "onClick:clear": _cache[1] || (_cache[1] = ($event) => (searchQuery.value = ''))
                                }, null, 8, ["modelValue"])
                            ]))
                            : _createCommentVNode("", true),
                    (!loading.value && filteredTransactions.value.length > 0)
                        ? (_openBlock(), _createElementBlock("ul", _hoisted_6, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredTransactions.value, (t) => {
                                return (_openBlock(), _createElementBlock("li", {
                                    key: t.id,
                                    class: "ut-row",
                                    onClick: ($event) => (onRowClick(t))
                                }, [
                                    (selectMode.value)
                                        ? (_openBlock(), _createElementBlock("span", {
                                            key: 0,
                                            class: _normalizeClass(["ut-row-check", { 'ut-row-check--on': selectedIds.value.has(t.id) }])
                                        }, [
                                            (selectedIds.value.has(t.id))
                                                ? (_openBlock(), _createElementBlock("img", {
                                                    key: 0,
                                                    src: require('@/assets/check-tick.svg'),
                                                    alt: ""
                                                }, null, 8, _hoisted_8))
                                                : _createCommentVNode("", true)
                                        ], 2))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("div", _hoisted_9, [
                                        _createElementVNode("span", _hoisted_10, _toDisplayString(t.merchantName || 'Unknown'), 1),
                                        _createElementVNode("span", _hoisted_11, _toDisplayString(formatRowTimestamp(t.timestamp)), 1)
                                    ]),
                                    _createElementVNode("div", _hoisted_12, [
                                        _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(state).formatter.format(Math.abs(t.amount))), 1),
                                        (!selectMode.value)
                                            ? (_openBlock(), _createElementBlock("img", {
                                                key: 0,
                                                class: "ut-row-arrow",
                                                src: require('@/assets/arrow-right.png'),
                                                alt: ""
                                            }, null, 8, _hoisted_14))
                                            : _createCommentVNode("", true)
                                    ])
                                ], 8, _hoisted_7));
                            }), 128))
                        ]))
                        : (!loading.value && uncategorisedCount.value === 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_15, " No more transactions to tag "))
                            : (!loading.value && filteredTransactions.value.length === 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_16, " No matches for \"" + _toDisplayString(searchQuery.value) + "\" ", 1))
                                : _createCommentVNode("", true),
                    (selectedIds.value.size > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                            _createVNode(MGButton, {
                                text: `Mark ${selectedIds.value.size} selected as...`,
                                arrow: "",
                                fullWidth: "",
                                noCaps: "",
                                onClick: openMarkSelectedDrawer
                            }, null, 8, ["text"])
                        ]))
                        : _createCommentVNode("", true)
                ]),
                _createVNode(ChangeCategoryDrawer, {
                    ref_key: "categoryDrawer",
                    ref: categoryDrawer,
                    onApply: onBulkCategoryApply
                }, null, 512),
                (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
                    _createVNode(_Transition, { name: "ut-toast" }, {
                        default: _withCtx(() => [
                            (toastCount.value > 0)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: "ut-toast",
                                    style: _normalizeStyle({ top: (16 + safeTop.value) + 'px' })
                                }, [
                                    _createElementVNode("img", {
                                        class: "ut-toast-tick",
                                        src: require('@/assets/check-tick.svg'),
                                        alt: ""
                                    }, null, 8, _hoisted_18),
                                    _createElementVNode("span", _hoisted_19, _toDisplayString(toastCount.value) + " transaction" + _toDisplayString(toastCount.value === 1 ? '' : 's') + " categorised ", 1)
                                ], 4))
                                : _createCommentVNode("", true)
                        ]),
                        _: 1
                    }),
                    _createVNode(_Transition, { name: "ut-toast" }, {
                        default: _withCtx(() => [
                            (limitToastVisible.value)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: "ut-toast ut-toast--warning",
                                    style: _normalizeStyle({ top: (16 + safeTop.value) + 'px' })
                                }, [
                                    _createElementVNode("span", { class: "ut-toast-text" }, " You can only select up to " + _toDisplayString(MAX_SELECTION) + " transactions at a time ")
                                ], 4))
                                : _createCommentVNode("", true)
                        ]),
                        _: 1
                    })
                ]))
            ], 64));
        };
    }
});
